import { useMemo } from "react";

import { Crisp } from "crisp-sdk-web";
import { useTranslations } from "next-intl";

import { BetaDescription } from "@/components/doc/right-sidebar/download-menu/BetaDescription";
import { ExportToOverleafButton } from "@/components/doc/right-sidebar/download-menu/ExportToOverleafButton";
import { Icon } from "@/components/icons/Icon";
import { Sidebar } from "@/components/sidebar";
import { Dropdown, type DropdownItem } from "@/components/sidebar/Dropdown";
import { trackEvent } from "@/utils/trackEvent";

import { DownloadLatexButton } from "./DownloadLatexButton";
import { DownloadPDFButton } from "./DownloadPDFButton";

import type { DocumentModel } from "@/models/DocumentModel";

function getMenuItems(doc: DocumentModel): DropdownItem[] {
  return [
    {
      component: ({ close }: { close: () => void }) => (
        <DownloadLatexButton doc={doc} onClick={close} />
      ),
    },
    {
      component: ({ close }: { close: () => void }) => (
        <DownloadPDFButton docId={doc.id} onClick={close} />
      ),
    },
    {
      component: ({ close }: { close: () => void }) => (
        <ExportToOverleafButton docId={doc.id} docPermissions={doc.permissions} onClick={close} />
      ),
      footer: () => (
        <BetaDescription
          onActionClick={() => {
            Crisp.chat.open();
            close();
          }}
        />
      ),
    },
  ];
}

export function DownloadMenu({ doc, isCollapsed }: { doc: DocumentModel; isCollapsed?: boolean }) {
  const t = useTranslations("sidebar.downloadMenu");

  const handleDownloadButtonClick = () => {
    trackEvent("download_menu_opened", { doc_id: doc.id! });
  };

  const items: DropdownItem[] = useMemo(() => getMenuItems(doc), [doc]);
  const handler = (
    <Sidebar.Button
      as="div"
      title={t("download")}
      // The menu button around it is the labelled control
      aria-label={undefined}
      disableTooltip={isCollapsed}
      icon={<Icon name="download/Download" />}
      onClick={handleDownloadButtonClick}
      hintKey="exportDocument"
    />
  );

  // A menu button in both states: the keyboard cannot reach a hover popover's trigger
  return (
    <Dropdown
      toggleButtonComponent={handler}
      toggleButtonLabel={t("download")}
      items={items}
      anchor={isCollapsed ? { to: "left end", gap: 14 } : undefined}
    />
  );
}
